import { useCallback, useMemo } from 'react'
import { useTimelineStore } from '@/features/effects/deps/timeline-contract'
import { useColorGradePresetsStore } from '@/features/effects/stores/color-grade-presets-store'
import {
  applyColorGradeSnapshotToEffects,
  buildColorGradeSnapshot,
  cloneColorGradeSnapshot,
  type ColorGradeSnapshotEntry,
} from '@/features/effects/utils/color-grade'
import { useGizmoStore } from '@/features/effects/deps/preview-contract'
import { useGradeClipboardStore } from '@/shared/state/grade-clipboard'
import type { TimelineItem } from '@/types/timeline'

interface UseColorGradeActionsResult {
  presets: ReturnType<typeof useColorGradePresetsStore.getState>['presets']
  canCopyGrade: boolean
  canPasteGrade: boolean
  canSavePreset: boolean
  copyGrade: () => boolean
  pasteGrade: () => boolean
  savePreset: (name: string) => boolean
  applyPreset: (presetId: string) => boolean
  removePreset: (presetId: string) => void
}

function findFirstAvailableGrade(items: readonly TimelineItem[]): ColorGradeSnapshotEntry[] | null {
  for (const item of items) {
    const snapshot = buildColorGradeSnapshot(item)
    if (snapshot.length > 0) {
      return snapshot
    }
  }

  return null
}

export function useColorGradeActions(items: readonly TimelineItem[]): UseColorGradeActionsResult {
  const updateItem = useTimelineStore((state) => state.updateItem)
  const clearPreview = useGizmoStore((state) => state.clearPreview)
  const presets = useColorGradePresetsStore((state) => state.presets)
  const savePresetEntry = useColorGradePresetsStore((state) => state.savePreset)
  const removePresetEntry = useColorGradePresetsStore((state) => state.removePreset)
  const setCopiedGrade = useGradeClipboardStore((state) => state.setGrade)
  const hasCopiedGrade = useGradeClipboardStore((state) => state.hasGrade())

  const currentGrade = useMemo(() => findFirstAvailableGrade(items), [items])
  const canCopyGrade = currentGrade !== null
  const canPasteGrade = hasCopiedGrade
  const canSavePreset = currentGrade !== null

  const applySnapshot = useCallback(
    (snapshot: readonly ColorGradeSnapshotEntry[]) => {
      if (snapshot.length === 0 || items.length === 0) {
        return false
      }

      const clonedSnapshot = cloneColorGradeSnapshot(snapshot)
      items.forEach((item) => {
        updateItem(item.id, {
          effects: applyColorGradeSnapshotToEffects(item.effects, clonedSnapshot),
        })
      })
      queueMicrotask(() => clearPreview())
      return true
    },
    [clearPreview, items, updateItem],
  )

  const copyGrade = useCallback(() => {
    if (!currentGrade) {
      return false
    }

    setCopiedGrade(cloneColorGradeSnapshot(currentGrade))
    return true
  }, [currentGrade, setCopiedGrade])

  const pasteGrade = useCallback(() => {
    const copiedGrade = useGradeClipboardStore.getState().grade
    if (!copiedGrade || copiedGrade.length === 0) {
      return false
    }

    return applySnapshot(copiedGrade)
  }, [applySnapshot])

  const savePreset = useCallback(
    (name: string) => {
      if (!currentGrade) {
        return false
      }

      return savePresetEntry(name, currentGrade) !== null
    },
    [currentGrade, savePresetEntry],
  )

  const applyPreset = useCallback(
    (presetId: string) => {
      const preset = useColorGradePresetsStore.getState().presets.find((entry) => entry.id === presetId)
      if (!preset) {
        return false
      }

      return applySnapshot(preset.grade)
    },
    [applySnapshot],
  )

  const removePreset = useCallback(
    (presetId: string) => {
      removePresetEntry(presetId)
    },
    [removePresetEntry],
  )

  return {
    presets,
    canCopyGrade,
    canPasteGrade,
    canSavePreset,
    copyGrade,
    pasteGrade,
    savePreset,
    applyPreset,
    removePreset,
  }
}
